import type { FunctionComponent } from 'react';
import { Inline, paletteHelper, radiusHelper, spacingHelper, Text } from '@property.com.au/ui-kit';
import styled, { css } from 'styled-components';
import type { MediaCount } from '../../MediaCountButtonsV2';
import { isNotNullOrUndefinedValue } from '../../../../../helpers/isNotNullOrUndefinedValue';
import { MediaCountsIconV2 } from '../MediaCountsIconV2';

const MediaButton = styled.button<{ $shouldChangeColorOnHover: boolean }>`
  background-color: ${paletteHelper('backgroundTertiaryInvertedStatic')};
  color: ${paletteHelper('textInverted')};
  padding: ${spacingHelper('extraSmall')} ${spacingHelper('small')};
  border-radius: ${radiusHelper('full')};
  height: ${spacingHelper('extraLarge')};
  display: flex;

  ${({ $shouldChangeColorOnHover }) =>
    $shouldChangeColorOnHover &&
    css`
      &:hover {
        cursor: pointer;
        background-color: ${paletteHelper('backgroundHoveredTertiaryInvertedStatic')};
      }

      &:focus {
        outline: 2px solid ${paletteHelper('borderFocusedInvertedStatic')};
        outline-offset: 2px;
      }

      &:focus:active {
        background-color: ${paletteHelper('backgroundPressedTertiaryInvertedStatic')};
        outline: none;
      }
    `};
`;

interface MediaCountButtonProps {
  count: MediaCount;
  onClickHandler?: (imageIndexFrom: number) => void;
}

export const MediaCountButtonV2: FunctionComponent<MediaCountButtonProps> = ({
  count: { iconNameMd, title, value, imageIndexFrom },
  onClickHandler,
}) => {
  const onMediaCountClickGoesToFirstImageOfMediaType = isNotNullOrUndefinedValue(imageIndexFrom);

  return (
    <MediaButton
      onClick={onClickHandler ? () => onClickHandler(imageIndexFrom ?? 0) : () => undefined}
      aria-label={`Go to ${title}`}
      $shouldChangeColorOnHover={onMediaCountClickGoesToFirstImageOfMediaType}
    >
      <Inline grow={false} gap="twoExtraSmall" title={title}>
        <MediaCountsIconV2 iconName={iconNameMd} title={title} />
        <Text variant="body02Bold">{value}</Text>
      </Inline>
    </MediaButton>
  );
};
